import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Lazy } from 'swiper';
import { Tooltip } from 'react-tooltip';
import SliderThumbs from './slider-thumbs';
import SlideUp from '../slide-up';
import BlurredBackdrop from '@components/blurred-backdrop';

import './style.scss';
import 'swiper/css';
import 'swiper/css/lazy';
import "swiper/css/effect-fade";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

const BlurredImageSlider = ({
    className = '',
    images = [],
    sizes = '100vw',
    withNavigation = false,
    loadPrevNext = false,
    thumbsPosition = "top",
    autoPlay = false,
    nested = false,
    slidesPerView = 1,
    spaceBetween = 0
}) => {
    const showNavigation = withNavigation && images?.length > 1;
    const [loadedImages, setLoadedImages] = useState(new Set());
    const [active, setActive] = useState(0);
    const swiperRef = useRef();
    const [showInfo, setShowInfo] = useState(null);


    const swiperKey = useMemo(() => 'swiper-blurred', []);

    const handleImageLoad = useCallback((imageIndex) => {
        setLoadedImages(prev => new Set([...prev, imageIndex]));
    }, []);

    const handleThumbClick = useCallback((index) => {
        if (swiperRef.current) {
            try {
                swiperRef.current.slideTo(index, 300);
            } catch (error) {
                console.error('Error calling slideTo:', error);
            }
        }
        setActive(index);
    }, []);

    const onSwiper = useCallback((s) => {
        swiperRef.current = s;
    }, []);

    const onSlideChange = useCallback(s => {
        if (withNavigation) {
            setActive(s.realIndex);
        }
    }, [withNavigation]);

    const handleInfoClick = useCallback((label, content) => {
        setShowInfo({ label, content });
    }, []);

    const isLongDescription = useCallback((parentId, containerId, creditId) => {
        const parent = document.getElementById(parentId);
        const container = document.getElementById(containerId);
        const credit = document.getElementById(creditId);

        if (!parent || !container || !credit) return false;

        const parentRect = parent.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const creditRect = credit.getBoundingClientRect();

        return containerRect.height + creditRect.height > parentRect.height;
    }, []);

    const getSwiper = useMemo(() => {
        return (
            <div key={swiperKey} className={`swiper-container ${slidesPerView > 1 ? 'mutiple-slides-per-view' : ''}`}>
                <Swiper
                    autoplay={{
                        enabled: autoPlay && images.length > 1,
                        delay: 5000
                    }}
                    className={`slider ${className} blurred-backdrop`}
                    effect={slidesPerView > 1 ? "slide" : "fade"}
                    initialSlide={0}
                    lazy={{
                        checkInView: false,
                        enabled: false,
                        loadPrevNext: loadPrevNext,
                    }}
                    loop={false}
                    modules={[Autoplay, EffectFade, Lazy]}
                    onSwiper={onSwiper}
                    onSlideChange={onSlideChange}
                    preloadImages={true}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    breakpoints={{
                        896: {
                            slidesPerView: slidesPerView > 1 ? 2.5 : 1,
                        },
                        1280: {
                            slidesPerView: slidesPerView
                        }
                    }}
                    speed={800}
                    runCallbacksOnInit={true}
                    nested={nested}
                >
                    {images?.map((image, i) => {
                        const isImageLoaded = loadedImages.has(i);

                        return (
                            <SwiperSlide key={`blurred-${i}`}>
                                {image && (
                                    <BlurredBackdrop
                                        image={image}
                                        sizes={sizes}
                                        customClass={(image.imageCredit || image.imageDesc || image.imageLabel) ? "has-info" : ""}
                                        onImageLoad={() => handleImageLoad(i)}
                                    />
                                )}

                                {(image.imageCredit || image.imageDesc || image.imageLabel) ? (
                                    <div className="media-info-bar" id={`tooltip-${image.UrlFragment}-${i}-parent`}>
                                        {image.imageDesc ? (
                                            <div>
                                                <div
                                                    data-tooltip-id={`tooltip-${image.UrlFragment}-${i}`}
                                                >
                                                    <FontAwesomeIcon icon={faInfoCircle} onClick={() => handleInfoClick(image.imageLabel, image.imageDesc)} />
                                                </div>
                                                <Tooltip className="tooltip-content" id={`tooltip-${image.UrlFragment}-${i}`} place="bottom" content={image.imageDesc} />
                                            </div>
                                        ) : null}
                                        <div className='media-info-text' id={`tooltip-${image.UrlFragment}-${i}-container`}>
                                            <>
                                                <p
                                                    data-tooltip-id={`label-tooltip-${image.UrlFragment}-${i}`}
                                                    id={`label-tooltip-${image.UrlFragment}-${i}-container`}
                                                    className='label-container'
                                                >
                                                    {typeof image.imageLabel === 'string' && image.imageLabel ? (
                                                        <span>{image.imageLabel}</span>
                                                    ) : image.imageLabel && typeof image.imageLabel === 'object' && image.imageLabel?.en_text ? (
                                                        <span>{image.imageLabel?.en_text}</span>
                                                    ) : ''}
                                                </p>
                                                {isLongDescription(`tooltip-${image.UrlFragment}-${i}-parent`, `label-tooltip-${image.UrlFragment}-${i}-container`, `credit-${image.UrlFragment}-${i}-container`) ? (
                                                    <Tooltip className="tooltip-content" id={`label-tooltip-${image.UrlFragment}-${i}`} place="bottom" content={image.imageLabel} />
                                                ) : null}
                                                <p id={`credit-${image.UrlFragment}-${i}-container`}>{image.imageCredit ? `Credit: ${image.imageCredit}` : null}</p>
                                            </>
                                        </div>
                                    </div>
                                ) : null}
                                {!isImageLoaded && <div className="swiper-lazy-preloader"></div>}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                {showInfo && (
                    <SlideUp header={showInfo.label} onClose={() => setShowInfo(null)} defaultOpen={true}>
                        <p className="slide-up-text">{showInfo.content}</p>
                    </SlideUp>
                )}
            </div>
        );
    }, [images, sizes, onSlideChange, onSwiper, loadPrevNext, className, autoPlay, nested, showInfo, handleInfoClick, isLongDescription, slidesPerView, spaceBetween, swiperKey, loadedImages, handleImageLoad]);

    return (
        <>
            {showNavigation && thumbsPosition === "top" && <SliderThumbs images={images} active={active} setActive={handleThumbClick} />}
            {getSwiper}
            {showNavigation && thumbsPosition === "bottom" && <SliderThumbs images={images} active={active} setActive={handleThumbClick} />}
        </>
    );
};

export default BlurredImageSlider; 